import { motion } from 'framer-motion';
import mobileShoppingImg from '../assets/mobile-shopping.jpeg';

const painPoints = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: '流量蒸發',
    description: '廣告成本年年上漲，點一次幾十塊，客人點完就走，錢直接蒸發。',
    stat: '廣告 CPC 年漲 30%+',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
    title: '一次性交易',
    description: '沒有自動追蹤、沒有再營銷，客戶來了留不住，毫無終身價值。',
    stat: '客戶流失率 > 80%',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: '人肉業務瓶頸',
    description: '靠人工確認訂單、對帳、算佣金，時間有上限，凌晨的訂單只能等明天。',
    stat: '每天僅能處理 8 小時',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: '口碑變現斷層',
    description: '產品好但缺乏裂變機制，客戶介紹新客沒有動力，無法變成現金流。',
    stat: '裂變轉化率 ≈ 0%',
  },
];

export default function PainPointsSection() {
  return (
    <section id="pain-points" className="relative py-24 lg:py-32 bg-warm-100">
      {/* Decorative top line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-hermes-500/30 to-transparent" />

      <div className="max-w-6xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 text-hermes-500 font-bold text-sm uppercase tracking-wider mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            致命痛點
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-hermes-900 mb-4">
            傳統電商的「四大獵殺鏈」
          </h2>
          <p className="text-lg text-warm-500 max-w-2xl mx-auto">
            為什麼舊模式註定越做越累？每一個問題都在默默吞噬你的利潤。
          </p>
        </motion.div>

        {/* Two-column layout: cards + image */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* Pain point cards - 2 columns */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {painPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group relative bg-warm-50 rounded-2xl p-6 border border-warm-300 shadow-sm hover:shadow-xl hover:border-hermes-500/30 transition-all duration-300"
              >
                {/* Number badge */}
                <div className="absolute top-4 right-4 text-5xl font-black text-warm-200 group-hover:text-hermes-100 transition-colors">
                  {String(index + 1).padStart(2, '0')}
                </div>

                <div className="relative">
                  {/* Icon */}
                  <div className="w-12 h-12 bg-hermes-50 text-hermes-500 rounded-xl flex items-center justify-center mb-4 group-hover:bg-hermes-500 group-hover:text-white transition-all duration-300">
                    {point.icon}
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-bold text-hermes-900 mb-2">{point.title}</h3>
                  <p className="text-warm-500 text-sm leading-relaxed mb-3">{point.description}</p>

                  {/* Stat */}
                  <div className="inline-flex items-center gap-2 bg-hermes-50 text-hermes-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {point.stat}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right side image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="hidden lg:flex items-center"
          >
            <div className="relative w-full">
              <div className="absolute inset-0 bg-gradient-to-br from-hermes-500/10 to-hermes-400/10 rounded-3xl blur-xl" />
              <img
                src={mobileShoppingImg}
                alt="手機網上購物 - 傳統電商痛點"
                className="relative w-full h-full object-cover rounded-3xl shadow-xl border border-warm-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-hermes-900/50 to-transparent rounded-3xl" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white font-bold text-lg">傳統電商困境</p>
                <p className="text-white/70 text-sm">你是否也正面對這些問題？</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom connector arrow */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex justify-center mt-16"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-warm-500 text-sm font-medium">你需要的解決方案</span>
            <svg className="w-6 h-6 text-hermes-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
